import {
  Ng1StateDeclaration,
  Ng1ViewDeclaration,
  StateRegistry,
  StateService,
} from "@uirouter/angularjs";
import { IServiceProvider, IQService, IPromise, Injectable } from "angular";
import {
  vsName as ServerUtilityName,
  ServerUtility,
} from "../../records/server/serverUtility";
import {
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import { Record } from "../../records/server/crud";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../application/UserProfile";
import {
  ChangeJournalObject,
  ChangeJournalObjectIdentifier,
} from "../../records/changeJournal/changeJournalViewComponent/models";
import { vsComponentName as changeJournalViewComponentName } from "../../records/changeJournal/changeJournalViewComponent";
import {
  ChangeJournalViewItemServiceComponentConfig,
  ChangeJournalViewItemServiceProvider,
  vsProviderName as ChangeJournalViewItemServiceProviderName,
} from "../../records/changeJournal/changeJournalViewComponent/itemObjectDetail";
import { ServiceResponse } from "../../records/server/query";
import { vsComponentName as changeJournalContractItemViewComponentName } from "./changeJournal/contractDetailView";

export const baseState = "main.contract.type";

export const createState = `${baseState}.create`;
export const gridState = `${baseState}.grid`;

export const infoState = `${baseState}.info`;
export const editState = `${infoState}.edit`;
export const infoRedirectState = `${infoState}.redirect`;

export const vsServiceName = "contractService";
export const vsProviderName = `${vsServiceName}Provider`;

export class ContractServiceProvider implements IServiceProvider {
  static $inject = [ChangeJournalViewItemServiceProviderName];
  constructor(
    private readonly changeJournalViewItemServiceProvider: ChangeJournalViewItemServiceProvider
  ) {}

  private readonly _contractTypes: IContractType[] = [];
  private readonly _contractTabs: IContractTab[] = [];

  addContractType(contractType: IContractType) {
    this._contractTypes.push(contractType);
    this.registerChangeJournal(contractType);
  }

  private registerChangeJournal(contractType: IContractType) {
    this.changeJournalViewItemServiceProvider.addTypeComponent(
      contractType.objectName,
      <ChangeJournalViewItemServiceComponentConfig>{
        componentName: changeJournalContractItemViewComponentName,
        resolveObjects: [
          "objectIdentifier",
          ServerUtilityName,
          (
            objectIdentifier: ChangeJournalObjectIdentifier,
            serverUtility: ServerUtility
          ) =>
            serverUtility
              .getById<ServiceResponse<any>>({
                id: objectIdentifier.ObjectId,
                url: ContractService.getDefaultBaseUrl(contractType.id),
              })
              .then((r): ChangeJournalObject[] => [
                {
                  id: objectIdentifier,
                  object: r,
                },
              ]),
        ],
        resolveInfoSref: (object: ChangeJournalObject) => ({
          sref: "main.contract.type.info.redirect",
          params: {
            type: contractType.id, // dynamic based on contractType
            contractInstanceId: object.object.ContractInstanceId,
          },
        }),
      }
    );
  }

  addContractTab(contractTab: IContractTab) {
    this._contractTabs.push(contractTab);
  }

  $get = [
    "$state",
    "$stateRegistry",
    FormViewServiceName,
    ServerUtilityName,
    UserProfileName,
    (
      $state: StateService,
      $stateRegistry: StateRegistry,
      formViewService: FormViewService,
      serverUtility: ServerUtility,
      userProfile: UserProfile
    ) => {
      this.addContractTab({
        uiSref: "changeLog",
        name: "Change Log",
        state: {
          name: `${infoState}.changeLog`,
          url: "/changeLog",
          views: {
            tabs: {
              component: changeJournalViewComponentName,
            },
          },
          resolve: {
            objects: [
              "contract",
              "contractType",
              "$q",
              "$injector",
              resolveChangeJournalObjects,
            ],
          },
        },
      });

      const defaultViews = {};

      this.registerContractTabs(
        $stateRegistry,
        infoState,
        this._contractTabs,
        defaultViews
      );

      const contractTypes: ContractTypeService[] = [];
      const contractService = new ContractService(
        $state,
        contractTypes,
        this._contractTabs,
        formViewService,
        serverUtility,
        userProfile
      );
      contractTypes.push(
        ...this._contractTypes.map((ct) => {
          const typeSpecificTabs = (ct.tabs ??= []);

          this.registerContractTabs(
            $stateRegistry,
            infoState,
            typeSpecificTabs,
            defaultViews
          );
          ct.tabs = [...typeSpecificTabs, ...this._contractTabs];
          return new ContractTypeService(contractService, ct);
        })
      );

      return contractService;
    },
  ];

  registerContractTabs(
    $stateRegistry: StateRegistry,
    basePath: string,
    tabs: IContractTab[],
    views?: { [key: string]: Ng1ViewDeclaration }
  ) {
    tabs.forEach((t) => {
      const stateName = (t.state.name ??= `${basePath}.${t.uiSref}`);
      t.state.views = { ...views, ...t.state.views };

      if (!$stateRegistry.get(stateName)) {
        $stateRegistry.register(t.state);
        this.registerContractTabs($stateRegistry, stateName, t.children ?? []);
      }
    });
  }
}

export class ContractService {
  constructor(
    private readonly $state: StateService,
    private readonly contractTypes: ContractTypeService[],
    private readonly contractTabs: IContractTab[],
    private readonly formViewService: FormViewService,
    private readonly serverUtility: ServerUtility,
    private readonly userProfile: UserProfile
  ) {}

  getContractType(id: string): ContractTypeService | undefined {
    return this.contractTypes.find((ct) => ct.id === id);
  }

  getContractTabs(): IContractTab[] {
    return this.contractTabs;
  }

  getContractTypes(): ContractTypeService[] {
    return this.contractTypes;
  }

  gridNavigate(type: string) {
    return this.$state.go(gridState, { type });
  }

  gridSref(type: string) {
    return gridState;
  }

  infoNavigate(type: string, contractInstanceId: number) {
    return this.$state.go(
      this.infoSref(type),
      { type, contractInstanceId },
      { reload: true }
    );
  }

  infoSref(type: string, tab?: IContractTab, routeParams?: any) {
    const contractType = this.getContractType(type);
    tab ??= contractType?.tabs?.at(0);

    const paramString = routeParams ? `(${JSON.stringify(routeParams)})` : "";

    if (tab) {
      return `${infoState}.${tab.uiSref}${paramString}`;
    }

    return infoState;
  }

  static getDefaultBaseUrl(typeId: string): string {
    return `/api/contract/${typeId}`;
  }

  getBaseUrl(type: string): string {
    const contractType = this.getContractType(type);
    if (!contractType) throw new Error(`Contract type ${type} not found.`);

    return (
      contractType.baseUrl ?? ContractService.getDefaultBaseUrl(contractType.id)
    );
  }

  editNavigate(type: string, contractInstanceId: number) {
    return this.$state.go(this.editSref(type), { type, contractInstanceId });
  }

  editSref(type: string) {
    return editState;
  }

  createNavigate(type: string) {
    return this.$state.go(this.createSref(type), { type });
  }

  createSref(type: string) {
    return createState;
  }

  hasSensitiveInfoPermission(type: string) {
    const contractType = this.getContractType(type);
    const permission = contractType?.sensitiveInfoPermission;
    return permission ? this.userProfile.hasPermission(permission) : false;
  }

  fetchContract<TContract extends Record>(
    type: string,
    contractInstanceId: number
  ) {
    const contractType = this.getContractType(type)!;

    return this.serverUtility.getById<TContract>({
      url: `/api/contract/${contractType.id}/byInstance`,
      id: contractInstanceId,
    });
  }

  cloneContract<TContract extends Record>(
    type: string,
    contractInstanceId: number
  ) {
    const contractType = this.getContractType(type)!;

    return this.serverUtility
      .sendApiRequest<TContract>({
        url: `/api/contract/${contractType.id}/byInstance/${contractInstanceId}/clone`,
        method: "GET",
      })
      .then((model) => {
        const clone: any = model;
        clone.ContractNumber =
          clone.ContractStartDate =
          clone.ContractEndDate =
          clone.ContractStatusId =
          clone.ContractStatus =
            undefined;
        return clone;
      });
  }

  submitContract<TContract extends Record & IHasContractInstanceId>(
    type: string,
    contract: TContract,
    formViewController: FormViewController
  ) {
    if (!contract) {
      throw new Error("Contract object is undefined.");
    }

    const contractType = this.getContractType(type)!;

    const newRecord = !contract.Id;
    const baseUrl = contractType.getBaseUrl();
    const apiEndpoint = newRecord ? baseUrl : `${baseUrl}/${contract.Id}`;

    return this.formViewService.submitForm<TContract>({
      formObject: contract,
      formViewController,
      method: newRecord ? "POST" : "PUT",
      url: apiEndpoint,
    });
  }

  private getFirstInfoState(contractType: IContractType) {
    return contractType.tabs?.at(0)?.uiSref ?? infoState;
  }
}

//do this so contract service is instantiated and routes get registered as early as possible
ContractServiceRun.$inject = [vsServiceName];
export function ContractServiceRun() {}

export class ContractTypeService implements IContractType {
  prepareClone?: (model: any) => any;

  constructor(
    private readonly contractService: ContractService,
    contractType: IContractType
  ) {
    Object.assign(this, contractType);

    this.formConfigName ??= contractType.name;
    this.gridConfigName ??= contractType.name;
    this.tabs ??= [];
  }

  resolveAdditionalChangeJournalObjects?: Injectable<
    (...args: any[]) => IPromise<ChangeJournalObject[]>
  >;

  baseUrl?: string;
  fetchContract(contractInstanceId: number) {
    return this.contractService.fetchContract(this.id, contractInstanceId);
  }

  cloneContract(contractInstanceId: number) {
    return this.contractService
      .cloneContract(this.id, contractInstanceId)
      .then((model) => (this.prepareClone ? this.prepareClone(model) : model));
  }

  submitContract(
    contract: Record & IHasContractInstanceId,
    formViewController: FormViewController
  ) {
    return this.contractService.submitContract(
      this.id,
      contract,
      formViewController
    );
  }

  infoSref(tab?: IContractTab, routeParams?: any) {
    return this.contractService.infoSref(this.id, tab, routeParams);
  }

  getBaseUrl() {
    return this.contractService.getBaseUrl(this.id);
  }

  infoNavigate(contractInstanceId: number) {
    return this.contractService.infoNavigate(this.id, contractInstanceId);
  }

  gridSref() {
    this.contractService.gridSref(this.id);
  }

  gridNavigate() {
    return this.contractService.gridNavigate(this.id);
  }

  editNavigate(contractInstanceId: number) {
    return this.contractService.editNavigate(this.id, contractInstanceId);
  }

  editSref() {
    return this.contractService.editSref(this.id);
  }

  createNavigate() {
    return this.contractService.createNavigate(this.id);
  }

  createSref() {
    return this.contractService.createSref(this.id);
  }

  hasSensitiveInfoPermission(): boolean {
    return this.contractService.hasSensitiveInfoPermission(this.id);
  }
  sensitiveInfoPermission?: string;

  id!: string;
  name!: string;
  formConfigName: string;
  gridConfigName: string;
  detailComponentName!: string;
  objectName!: string;
  tabs?: IContractTab[];
}

export interface IContractType {
  id: string;
  name: string;
  detailComponentName: string;
  formConfigName: string;
  gridConfigName: string;
  objectName: string;
  tabs?: IContractTab[];
  sensitiveInfoPermission?: string | string[];
  baseUrl?: string;
  prepareClone?: (model: any) => any;
  resolveAdditionalChangeJournalObjects?: Injectable<
    (...args: any[]) => IPromise<ChangeJournalObject[]>
  >;
}

export interface IHasContractInstanceId {
  ContractInstanceId: number;
}

export interface IContractTab {
  uiSref: string;
  name: string;
  state: Ng1StateDeclaration;
  children?: IContractTab[];
}

function resolveChangeJournalObjects(
  contract: any,
  contractType: ContractTypeService,
  $q: IQService,
  $injector: angular.auto.IInjectorService
): IPromise<ChangeJournalObject[]> {
  const contractObjectPromise = $q.when([
    {
      id: {
        ObjectId: contract.Id,
        ObjectName: contractType.objectName,
      },
      object: contract,
    },
  ]);

  const additionalChangeJournalObject =
    contractType.resolveAdditionalChangeJournalObjects
      ? $injector.invoke(
          contractType.resolveAdditionalChangeJournalObjects,
          undefined,
          {
            contract,
            contractType,
          }
        )
      : $q.when(<ChangeJournalObject[]>[]);

  return $q
    .all([contractObjectPromise, additionalChangeJournalObject])
    .then((resolved) => [...resolved[0], ...resolved[1]]);
}
